/**
 * Create Item Node Configuration - creates a new ALM item in a configured target tracker,
 * with each field of the new item filled from a field mapping (literal, workflow input,
 * upstream node output or source item field), optionally linked back to the source item.
 *
 * Maps to CreateItemStep (raiqon/schemas/workflow.py).
 */

import React, { useEffect, useState } from 'react';
import { pipelineApi } from '../api/client';
import type { PipelineNode, PipelineDefinition } from '../types/pipeline';
import { AlmTargetPicker } from './AlmTargetPicker';
import type { AlmTarget } from './AlmTargetPicker';
import { ValueBindingEditor, isBindingComplete, normalizeBinding } from './ValueBindingEditor';
import type { OutputValueBindingConfig } from './ValueBindingEditor';
import { PlusIcon } from './icons';
import './PushbackConfig.css';
import './CreateItemConfig.css';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface CreateItemFieldMappingConfig {
  target_field: string;
  value: OutputValueBindingConfig;
  required: boolean;
}

export interface CreateItemNodeConfig {
  target_scope_id: string;
  target_project_id: string;
  target_type_id: string;
  field_mappings: CreateItemFieldMappingConfig[];
  cardinality: 'per_item' | 'aggregate';
  link_to_source: boolean;
  link_reference_type: string;
  link_field_id: string;
}

export const DEFAULT_CREATE_ITEM_FIELD_MAPPINGS: CreateItemFieldMappingConfig[] = [
  { target_field: 'rq_summary', value: { source: 'step_output' }, required: true },
  { target_field: 'rq_description', value: { source: 'step_output' }, required: false },
];

interface CreateItemConfigProps {
  node: PipelineNode;
  pipeline: PipelineDefinition | null;
  availableWorkflowInputs: string[];
  onSave: (config: CreateItemNodeConfig) => void;
  onClose: () => void;
}

export const CreateItemConfig: React.FC<CreateItemConfigProps> = ({
  node,
  pipeline,
  availableWorkflowInputs,
  onSave,
  onClose,
}) => {
  const existing = node.config as Partial<CreateItemNodeConfig> | undefined;

  const [target, setTarget] = useState<AlmTarget>({
    scopeId: existing?.target_scope_id ?? '',
    projectId: existing?.target_project_id ?? '',
    typeId: existing?.target_type_id ?? '',
  });
  const [mappings, setMappings] = useState<CreateItemFieldMappingConfig[]>(
    existing?.field_mappings?.length ? existing.field_mappings : DEFAULT_CREATE_ITEM_FIELD_MAPPINGS
  );
  const [cardinality, setCardinality] = useState<CreateItemNodeConfig['cardinality']>(
    existing?.cardinality ?? 'per_item'
  );
  const [linkToSource, setLinkToSource] = useState<boolean>(existing?.link_to_source ?? false);
  const [linkReferenceType, setLinkReferenceType] = useState<string>(existing?.link_reference_type ?? 'ITEM_CREATED');
  const [linkFieldId, setLinkFieldId] = useState<string>(existing?.link_field_id ?? 'RQ_N/A');
  const [targetFields, setTargetFields] = useState<string[]>(['rq_summary', 'rq_description']);
  const [itemFields, setItemFields] = useState<string[]>([]);

  const isAggregate = cardinality === 'aggregate';
  const targetScopeValid = UUID_PATTERN.test(target.scopeId.trim());

  // Target-field suggestions come from what's already synced for that tracker.
  useEffect(() => {
    if (!targetScopeValid || !target.typeId.trim()) return;
    let cancelled = false;
    pipelineApi
      .getTargetFields(target.scopeId.trim(), target.typeId.trim())
      .then((fields) => {
        if (!cancelled) setTargetFields(fields);
      })
      .catch(() => {
        // Suggestions only - the target field stays free-text.
      });
    return () => {
      cancelled = true;
    };
  }, [target.scopeId, target.typeId, targetScopeValid]);

  useEffect(() => {
    pipelineApi
      .getOutputItemFieldNames()
      .then(setItemFields)
      .catch(() => setItemFields([]));
  }, []);

  const updateMapping = (index: number, patch: Partial<CreateItemFieldMappingConfig>) => {
    setMappings((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const targetFieldNames = mappings.map((m) => m.target_field.trim());
  const duplicateTargetFields = new Set(targetFieldNames.filter((f, i) => f && targetFieldNames.indexOf(f) !== i));

  const mappingErrors = mappings.map((m) => {
    if (!m.target_field.trim()) return 'Target field is required';
    if (duplicateTargetFields.has(m.target_field.trim())) return 'Target field is mapped more than once';
    if (!isBindingComplete(m.value)) return 'Value source is incomplete';
    if (isAggregate && m.value.source === 'item_field') return 'Source item fields are unavailable once per run';
    return null;
  });

  const canSave =
    targetScopeValid &&
    target.projectId.trim() !== '' &&
    target.typeId.trim() !== '' &&
    mappings.length > 0 &&
    mappingErrors.every((e) => e === null);

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      target_scope_id: target.scopeId.trim(),
      target_project_id: target.projectId.trim(),
      target_type_id: target.typeId.trim(),
      field_mappings: mappings.map((m) => ({
        target_field: m.target_field.trim(),
        value: normalizeBinding(m.value),
        required: m.required,
      })),
      cardinality,
      link_to_source: !isAggregate && linkToSource,
      link_reference_type: linkReferenceType.trim() || 'ITEM_CREATED',
      link_field_id: linkFieldId.trim() || 'RQ_N/A',
    });
    onClose();
  };

  return (
    <div className="pushback-config create-item-config">
      <div className="config-header">
        <div>
          <h3>{node.label || 'Create Item'}</h3>
          <p className="section-description">Creates a new item in the target tracker from mapped field values</p>
        </div>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="config-body">
        <div className="config-section">
          <h4 className="create-item-step-title">1. Target tracker</h4>
          <AlmTargetPicker value={target} onChange={setTarget} />
          {target.scopeId.trim() !== '' && !targetScopeValid && (
            <p className="field-error">Scope ID must be a valid UUID.</p>
          )}
        </div>

        <div className="config-section">
          <h4 className="create-item-step-title">2. Field mapping</h4>
          <datalist id={`${node.id}-target-fields`}>
            {targetFields.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
          {mappings.map((mapping, index) => (
            <div key={index} className="field-mapping-row">
              <div className="field-mapping-header">
                <input
                  type="text"
                  className="condition-input"
                  list={`${node.id}-target-fields`}
                  value={mapping.target_field}
                  onChange={(e) => updateMapping(index, { target_field: e.target.value })}
                  placeholder="Target field, e.g. rq_summary"
                />
                <label className="field-mapping-required">
                  <input
                    type="checkbox"
                    checked={mapping.required}
                    onChange={(e) => updateMapping(index, { required: e.target.checked })}
                  />
                  Required
                </label>
                <button
                  className="field-mapping-remove"
                  onClick={() => setMappings((prev) => prev.filter((_, i) => i !== index))}
                  disabled={mappings.length === 1}
                  title="Remove field"
                >
                  ×
                </button>
              </div>
              <ValueBindingEditor
                idPrefix={`${node.id}-mapping-${index}`}
                binding={mapping.value}
                onChange={(value) => updateMapping(index, { value })}
                pipeline={pipeline}
                nodeId={node.id}
                availableWorkflowInputs={availableWorkflowInputs}
                itemFields={itemFields}
                allowItemField={!isAggregate}
              />
              {mappingErrors[index] && <p className="field-error">{mappingErrors[index]}</p>}
            </div>
          ))}
          <button
            className="button-secondary field-mapping-add"
            onClick={() =>
              setMappings((prev) => [...prev, { target_field: '', value: { source: 'literal' }, required: false }])
            }
          >
            <PlusIcon /> Add field
          </button>
        </div>

        <div className="config-section">
          <h4 className="create-item-step-title">3. How many items</h4>
          <div className="condition-row">
            <select
              className="condition-input"
              value={cardinality}
              onChange={(e) => setCardinality(e.target.value as CreateItemNodeConfig['cardinality'])}
            >
              <option value="per_item">One item per source item</option>
              <option value="aggregate">One item per workflow run</option>
            </select>
          </div>
        </div>

        <div className="config-section">
          <h4 className="create-item-step-title">4. Link back to source item</h4>
          <label className="field-mapping-required">
            <input
              type="checkbox"
              checked={!isAggregate && linkToSource}
              disabled={isAggregate}
              onChange={(e) => setLinkToSource(e.target.checked)}
            />
            Create a reference from the source item to the new item
          </label>
          {isAggregate && <p className="field-hint">Not available when creating one item per run.</p>}
          {!isAggregate && linkToSource && (
            <>
              <div className="condition-row">
                <label>Reference Type</label>
                <input
                  type="text"
                  className="condition-input"
                  value={linkReferenceType}
                  onChange={(e) => setLinkReferenceType(e.target.value)}
                />
              </div>
              <div className="condition-row">
                <label>Field ID</label>
                <input
                  type="text"
                  className="condition-input"
                  value={linkFieldId}
                  onChange={(e) => setLinkFieldId(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className="config-help">
          <h4>How this works</h4>
          <ul>
            <li>
              <strong>Upstream node output</strong> reads a field of a connected node's result. For agents with a
              JSON output schema, pick one of its keys; <code>response</code> is the raw text.
            </li>
            <li>
              A <strong>required</strong> field that resolves empty fails the step instead of creating an incomplete
              item.
            </li>
            <li>Re-running the same execution re-uses the same item reference, so it won't create duplicates.</li>
          </ul>
        </div>
      </div>

      <div className="config-footer">
        <button className="button-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="button-primary" onClick={handleSave} disabled={!canSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
};
